import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { CoursePlayerLearnerModalComponent, CoursePlayerLearnerModalData } from '../../shared/components/course-player-learner-modal/course-player-learner-modal.component';
import { CourseListDataService } from '../course-list-data.service';

/**
 * Route Wrapper for Course Player
 *
 * This component opens the course player modal when navigating to /courses/:id.
 * The modal includes a decorative "My Courses" header that shows through the glass header.
 *
 * When the modal closes, it navigates back to the course list (or, KAN-679, to
 * the certificate the learner chose to view from the course summary).
 */
@Component({
  selector: 'app-course-player-route-wrapper',
  standalone: true,
  imports: [CoursePlayerLearnerModalComponent],
  template: `
    <!-- Modal with decorative background header -->
    @if (modalData) {
      <app-course-player-learner-modal
        [data]="modalData"
        (onClose)="handleModalClose($event)">
      </app-course-player-learner-modal>
    }
    `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class CoursePlayerRouteWrapperComponent implements OnInit {
  modalData: CoursePlayerLearnerModalData | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private courseListData: CourseListDataService
  ) {}

  ngOnInit(): void {
    // Get course ID from route params
    const courseId = this.route.snapshot.paramMap.get('id');

    if (!courseId) {
      // No course ID, navigate back to course list
      this.router.navigate(['/courses']);
      return;
    }

    // Get optional query params
    const queryParams = this.route.snapshot.queryParams;
    const startItemId = queryParams['module']; // Support Continue functionality

    // Set up modal data
    this.modalData = {
      courseId,
      startItemId
    };
  }

  /**
   * Handle modal close event
   * Navigate back to course list
   */
  handleModalClose(event: { completed?: boolean; lastItemId?: string; orgMismatch?: boolean; accessDenied?: boolean; certificateId?: string }): void {
    console.log('[CoursePlayerWrapper] Modal closed:', event);

    // Clear stale "last accessed module" cache so Continue button fetches fresh progress
    if (this.modalData?.courseId) {
      this.courseListData.lastAccessedModule.delete(this.modalData.courseId);
    }

    // KAN-679: the summary's "View Certificate" closes straight to the certificate.
    if (event.certificateId) {
      this.router.navigate(['/certificates', event.certificateId]);
      return;
    }

    // Use replaceUrl on org mismatch (KAN-122) or a revoked-access deep-link
    // (KAN-415) to prevent the back button returning to the dead course URL.
    const replaceUrl = !!(event.orgMismatch || event.accessDenied);
    this.router.navigate(['/courses'], replaceUrl ? { replaceUrl: true } : {});
  }
}
